import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, Sparkles, Gift } from "lucide-react";

export default function ReferralCard({ email }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const target = email;

  useEffect(() => {
    if (!target) return;
    api.get(`/referrals/me?email=${encodeURIComponent(target)}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [target]);

  if (loading) return null;
  if (!data) return null;
  if (data.paused) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="referral-paused">
        <div className="flex items-center gap-2 mb-2 text-[#A1A1AA] text-xs uppercase tracking-[0.22em]">
          <Gift size={13}/> Referral program
        </div>
        <p className="text-sm text-[#F5F2EB]">Referral rewards are paused right now. Your code{data.code ? ` ${data.code}` : ""} and any earned credits are saved and will work again when the program resumes.</p>
      </div>
    );
  }
  if (data.has_code === false) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="referral-locked">
        <div className="flex items-center gap-2 mb-2 text-[#A1A1AA] text-xs uppercase tracking-[0.22em]">
          <Gift size={13}/> Referral program
        </div>
        <p className="text-sm text-[#F5F2EB]">{data.reason}</p>
      </div>
    );
  }

  const site = window.location.origin;
  const shareUrl = `${site}/book?ref=${data.code}`;
  const shareText = `I just rode with Delux Limo — try them and get 15% off your first ride with my code ${data.code}: ${shareUrl}`;
  const credits = data.credits_unused || 0;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Delux Limo", text: shareText, url: shareUrl });
      } catch {/* dismissed */}
    } else {
      copy(shareText, "Share message");
    }
  };

  return (
    <div className="border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-8" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em]">
        <Gift size={13}/> Your referral code
      </div>
      <div className="flex flex-wrap items-end gap-6 mb-6">
        <div>
          <div className="font-serif text-4xl md:text-5xl text-[#D4AF37] tracking-[2px]" data-testid="referral-code">{data.code}</div>
          <p className="text-sm text-[#A1A1AA] mt-2 max-w-md">
            Share with friends — they get <b className="text-[#D4AF37]">15% off</b> their first ride,
            and you earn <b className="text-[#D4AF37]">10% back</b> for your next one.
          </p>
        </div>
        <div className="md:ml-auto flex flex-col items-end">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Unused credits</div>
          <div className="font-serif text-4xl text-[#F5F2EB]" data-testid="referral-credits">{credits}<span className="text-base text-[#A1A1AA]"> / 3</span></div>
          {credits > 0 && (
            <div className="text-[10px] text-[#D4AF37] mt-1 flex items-center gap-1">
              <Sparkles size={10}/> Auto-applied at checkout
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button data-testid="copy-code" onClick={() => copy(data.code, "Code")} className="btn-ghost text-[11px] !py-2">
          <Copy size={12}/> Copy code
        </button>
        <button data-testid="copy-link" onClick={() => copy(shareUrl, "Link")} className="btn-ghost text-[11px] !py-2">
          <Copy size={12}/> Copy link
        </button>
        <button data-testid="share-referral" onClick={share} className="btn-gold text-[11px] !py-2">
          <Share2 size={12}/> Share
        </button>
      </div>

      {data.redemptions && data.redemptions.length > 0 && (
        <div className="pt-5 border-t border-white/10">
          <div className="overline text-[#A1A1AA] mb-3">Friends who rode</div>
          <div className="space-y-2">
            {data.redemptions.slice(0, 5).map((r, i) => (
              <div key={i} className="flex justify-between text-xs">
                <span className="text-[#F5F2EB]">{r.friend_email}</span>
                <span className="text-[#71717A]">{new Date(r.redeemed_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
